import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { sendTaskAssignedEmail } from "../utils/taskemailjs";
import {
  PlusCircle,
  Filter,
  CheckCircle2,
  Clock3,
  Trash2,
  SendHorizontal,
  MessageSquareText,
  CalendarDays,
  Users2,
} from "lucide-react";
import "./TaskWidget.css";

function safeDate(d) {
  if (!d) return null;
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? null : x;
}

function formatAssignee(m) {
  if (!m) return "—";
  const first = (m.first_name || "").trim();
  const last = (m.last_name || "").trim();
  const full = `${first} ${last}`.trim();
  const email = (m.email || m.invited_email || "").trim();

  // Prefer name/email; only show user_id if we truly have nothing else
  if (full && email) return `${full} • ${email}`;
  if (full) return full;
  if (email) return email;
  return m.user_id || "—";
}

export default function TaskWidget() {
  const { companyId, members, companyRole } = useCompany();

  const [tasks, setTasks] = useState([]);
  const [commentsByTask, setCommentsByTask] = useState({});
  const [showModal, setShowModal] = useState(false);

  const [newTask, setNewTask] = useState({
    text: "",
    dueDate: "",
    description: "",
    assignedTo: "",
  });

  const [newComments, setNewComments] = useState({});
  const [filterRange, setFilterRange] = useState({ start: "", end: "" });
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 4;

  // Enriched members (name/email) from profiles
  const [memberOptions, setMemberOptions] = useState([]);

  // Build a fast lookup for assignees by user_id
  const memberByUserId = useMemo(() => {
    const map = new Map();
    (memberOptions || []).forEach((m) => map.set(m.user_id, m));
    // fallback to context members if memberOptions is empty or missing someone
    (members || []).forEach((m) => {
      if (!map.has(m.user_id)) map.set(m.user_id, m);
    });
    return map;
  }, [memberOptions, members]);

  // Helper: choose the correct task key (supports either schema)
  function taskKey(t) {
    return t?.task_id || t?.id;
  }

  // ✅ 0) Load members with profiles (2-step, reliable)
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      // Step 1: company_members
      const { data: cm, error: cmErr } = await supabase
        .from("company_members")
        .select("user_id, role, invited_email")
        .eq("company_id", companyId);

      if (cmErr) {
        console.warn("TaskWidget: failed to load company_members:", cmErr);
        // fallback to context members
        setMemberOptions(
          (members || []).map((m) => ({
            user_id: m.user_id,
            role: m.role,
            invited_email: m.invited_email || null,
            email: m.email || null,
            first_name: m.first_name || null,
            last_name: m.last_name || null,
          }))
        );
        return;
      }

      const userIds = (cm || []).map((r) => r.user_id).filter(Boolean);

      // Step 2: profiles
      let profilesById = {};
      if (userIds.length) {
        const { data: ps, error: psErr } = await supabase
          .from("profiles")
          .select("id, first_name, last_name, email")
          .in("id", userIds);

        if (psErr) {
          console.warn("TaskWidget: failed to load profiles:", psErr);
        } else {
          (ps || []).forEach((p) => {
            profilesById[p.id] = p;
          });
        }
      }

      // Merge
      const enriched = (cm || []).map((row) => {
        const p = profilesById[row.user_id] || {};
        return {
          user_id: row.user_id,
          role: row.role,
          invited_email: row.invited_email || null,
          first_name: p.first_name || null,
          last_name: p.last_name || null,
          email: p.email || null,
        };
      });

      setMemberOptions(enriched);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // 1) Load tasks + comments (fixed)
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      const { data: ts, error: taskErr } = await supabase
        .from("tasks")
        .select("*")
        .eq("company_id", companyId)
        .order("due_date", { ascending: true });

      if (taskErr) {
        console.error("TaskWidget load tasks error:", taskErr);
        return;
      }

      const list = ts || [];
      setTasks(list);
      setFilteredTasks(list);

      const ids = list.map((t) => taskKey(t)).filter(Boolean);
      if (!ids.length) {
        setCommentsByTask({});
        return;
      }

      const { data: cms, error: cmErr } = await supabase
        .from("tasks_comments")
        .select("*")
        .in("task_id", ids)
        .order("created_at", { ascending: true });

      if (cmErr) {
        console.error("TaskWidget load comments error:", cmErr);
        return;
      }

      const byTask = {};
      (cms || []).forEach((c) => {
        (byTask[c.task_id] ||= []).push(c);
      });
      setCommentsByTask(byTask);
    })();
  }, [companyId]);

  // 2) Filtering handler
  const handleFilter = () => {
    const start = safeDate(filterRange.start);
    const end = safeDate(filterRange.end);

    const filtered = (tasks || []).filter((t) => {
      const d = safeDate(t.due_date);
      if (!d) return false;

      if (start && d < start) return false;
      if (end) {
        const eod = new Date(end);
        eod.setHours(23, 59, 59, 999);
        if (d > eod) return false;
      }
      return true;
    });

    setFilteredTasks(filtered);
    setCurrentPage(1);
  };

  // 3) Pagination slice
  const displayed = filteredTasks.slice(
    (currentPage - 1) * tasksPerPage,
    currentPage * tasksPerPage
  );

  // 4) Add Task
  const handleAddTask = async () => {
    const { text, dueDate, description, assignedTo } = newTask;

    if (!text || !dueDate || !assignedTo) {
      return alert("Please fill in description, due date, and assignee.");
    }

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) {
      return alert("You must be signed in to add a task.");
    }

    const payload = {
      company_id: companyId,
      created_by: user.id,
      user_id: assignedTo, // keep (existing feature)
      assigned_to: assignedTo, // keep (existing feature)
      text,
      due_date: dueDate,
      subDescription: description,
      completed: false,
    };

    const { data: createdTasks, error } = await supabase
      .from("tasks")
      .insert([payload])
      .select();

    if (error) return alert("Error adding task: " + error.message);

    const created = Array.isArray(createdTasks) ? createdTasks[0] : null;
    if (!created) return alert("Task was not returned from the server.");

    const sortOpenFirst = (a, b) =>
      a.completed === b.completed ? 0 : a.completed ? 1 : -1;

    setTasks((prev) => [...prev, created].sort(sortOpenFirst));
    setFilteredTasks((prev) => [...prev, created].sort(sortOpenFirst));

    setShowModal(false);
    setNewTask({ text: "", dueDate: "", description: "", assignedTo: "" });

    const assignee = memberByUserId.get(assignedTo);
    const toEmail = assignee?.email || assignee?.invited_email;
    if (toEmail) {
      sendTaskAssignedEmail(toEmail, { taskText: text, dueDate }).catch(
        console.error
      );
    }
  };

  // 5) Add Comment
  const handleAddComment = async (taskUuid) => {
    const txt = (newComments[taskUuid] || "").trim();
    if (!txt) return;

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError || !user) return alert("You must be signed in to comment.");

    const { data: cms, error } = await supabase
      .from("tasks_comments")
      .insert([{ task_id: taskUuid, user_id: user.id, comment: txt }])
      .select();

    if (error) return alert("Error adding comment: " + error.message);

    const cm = Array.isArray(cms) ? cms[0] : null;
    if (!cm) return alert("Comment was not returned from the server.");

    setCommentsByTask((prev) => ({
      ...prev,
      [taskUuid]: [...(prev[taskUuid] || []), cm],
    }));
    setNewComments((prev) => ({ ...prev, [taskUuid]: "" }));
  };

  // 6) Mark Complete
  const handleMarkComplete = async (id) => {
    await supabase.from("tasks").update({ completed: true }).eq("id", id);
    setTasks((ts) =>
      (ts || []).map((t) => (t.id === id ? { ...t, completed: true } : t))
    );
    setFilteredTasks((ts) =>
      (ts || []).map((t) => (t.id === id ? { ...t, completed: true } : t))
    );
  };

  // 7) Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await supabase.from("tasks").delete().eq("id", id);
    setTasks((ts) => (ts || []).filter((t) => t.id !== id));
    setFilteredTasks((ts) => (ts || []).filter((t) => t.id !== id));
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.25 } },
  };

  const totalPages = Math.ceil((filteredTasks?.length || 0) / tasksPerPage);

  return (
    <motion.div
      className="panel"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <div className="panel-head">
        <h3>Task Manager</h3>
        <div className="head-right">
          <div className="pill-count">
            <Clock3 size={14} />{" "}
            <span>{(tasks || []).filter((t) => !t.completed).length} open</span>
          </div>
          <div className="pill-count ok">
            <CheckCircle2 size={14} />{" "}
            <span>{(tasks || []).filter((t) => t.completed).length} done</span>
          </div>
        </div>
      </div>

      {/* Filters + New */}
      <div className="task-toolbar">
        <div className="filter-container">
          <CalendarDays size={16} className="muted-ico" />
          <input
            type="date"
            value={filterRange.start}
            onChange={(e) =>
              setFilterRange((fr) => ({ ...fr, start: e.target.value }))
            }
          />
          <span>to</span>
          <input
            type="date"
            value={filterRange.end}
            onChange={(e) =>
              setFilterRange((fr) => ({ ...fr, end: e.target.value }))
            }
          />
          <button onClick={handleFilter} className="btn-ghost small">
            <Filter size={14} /> Filter
          </button>
        </div>

        <button onClick={() => setShowModal(true)} className="btn-primary">
          <PlusCircle size={16} /> New Task
        </button>
      </div>

      {/* Table */}
      <div className="task-table">
        <div className="table-header">
          <span>Description</span>
          <span>Due</span>
          <span>Status</span>
          <span>Assignee</span>
          <span className="center">Actions</span>
        </div>

        <AnimatePresence initial={false}>
          {displayed.map((t) => {
            const key = taskKey(t);
            const due = safeDate(t.due_date);
            const assignee =
              memberByUserId.get(t.assigned_to) ||
              memberByUserId.get(t.user_id) ||
              null;

            return (
              <motion.div
                key={t.id}
                className="table-row"
                variants={fadeUp}
                initial="hidden"
                animate="show"
                exit={{ opacity: 0 }}
              >
                <div className="desc">
                  <strong>{t.text}</strong>
                  {t.subDescription && (
                    <small className="muted">{t.subDescription}</small>
                  )}
                </div>

                <span>{due ? due.toLocaleDateString() : "—"}</span>

                <span>
                  <span className={`badge ${t.completed ? "done" : "pending"}`}>
                    {t.completed ? (
                      <CheckCircle2 size={12} />
                    ) : (
                      <Clock3 size={12} />
                    )}
                    {t.completed ? "Done" : "Pending"}
                  </span>
                </span>

                <span className="assignee">
                  <Users2 size={14} />
                  {formatAssignee(assignee)}
                </span>

                <span className="center">
                  {!t.completed && (
                    <button
                      className="icon-btn ok"
                      title="Mark complete"
                      onClick={() => handleMarkComplete(t.id)}
                    >
                      <CheckCircle2 size={16} />
                    </button>
                  )}
                  {companyRole === "admin" && (
                    <button
                      className="icon-btn danger"
                      title="Delete task"
                      onClick={() => handleDelete(t.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </span>

                {/* Comments */}
                <div className="comments">
                  {(commentsByTask[key] || []).map((c) => (
                    <div key={c.id} className="comment-row">
                      <MessageSquareText size={14} />
                      <small>
                        <em>{new Date(c.created_at).toLocaleTimeString()}</em> —{" "}
                        {c.comment}
                      </small>
                    </div>
                  ))}

                  <div className="add-comment">
                    <input
                      placeholder="Add comment…"
                      value={newComments[key] || ""}
                      onChange={(e) =>
                        setNewComments((nc) => ({
                          ...nc,
                          [key]: e.target.value,
                        }))
                      }
                    />
                    <button
                      className="icon-btn"
                      title="Post comment"
                      onClick={() => handleAddComment(key)}
                      disabled={!key}
                    >
                      <SendHorizontal size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {!displayed.length && (
          <p className="muted empty">No tasks found for the selected range.</p>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* New Task Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="modal-content glass"
              initial={{ y: 18, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 18, opacity: 0 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-label="Create new task"
            >
              <h3>New Task</h3>

              <label className="stack">
                Description
                <input
                  placeholder="Short title / summary"
                  value={newTask.text}
                  onChange={(e) =>
                    setNewTask((nt) => ({ ...nt, text: e.target.value }))
                  }
                />
              </label>

              <div className="grid-2">
                <label className="stack">
                  Due date
                  <input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) =>
                      setNewTask((nt) => ({ ...nt, dueDate: e.target.value }))
                    }
                  />
                </label>

                <label className="stack">
                  Assign to
                  <select
                    value={newTask.assignedTo}
                    onChange={(e) =>
                      setNewTask((nt) => ({
                        ...nt,
                        assignedTo: e.target.value,
                      }))
                    }
                  >
                    <option value="">— select member —</option>
                    {(memberOptions?.length
                      ? memberOptions
                      : members || []
                    ).map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {formatAssignee(m)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="stack">
                Details
                <textarea
                  placeholder="Add additional notes or acceptance criteria…"
                  value={newTask.description}
                  onChange={(e) =>
                    setNewTask((nt) => ({
                      ...nt,
                      description: e.target.value,
                    }))
                  }
                  rows={4}
                />
              </label>

              <div className="modal-actions">
                <button className="btn-primary" onClick={handleAddTask}>
                  Create task
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
